import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import {
  useDuplicateSectionCeleryMutation,
  usePatchAhuUnitMutation,
} from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardSection, IAhuBoardTunnel, IAhuBoardUnit, ICmsAhuDoc } from 'models';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { clearSelection } from 'app/slices/ahuBoardSlice';
import {
  getPossibleBindingsForInsertion,
  type InsertionBinding,
} from './insertionBindings';
import { getPossibleHybridBindings } from './hybridInsertionBindings';
import {
  applySign,
  defaultOffsetForNewTunnel,
  getTunnelPhysicalSpan,
  type XOffsetSign,
} from './hybridXOffset';
import {
  DEFAULT_SECTION_LENGTH,
  createLugsForNewTunnel,
  getOrCreateTunnelFromIndex,
  refetchLugPlacementsAfterSectionInsert,
  type HybridTunnelTarget,
} from '../boardActions';
import { validateTunnelOnlyComponents } from '../validation/validationController';
import { holdCanvasRedrawBatchFor } from '../three/canvasRedrawScheduler';
import { analyticsComponentName, track } from 'app/analytics';

/**
 * The DIRECTION column's two options, verbatim from
 * insert_component.popup.tmpl:86-89 (single tunnel — arrow AFTER the label,
 * separated by an EN SPACE) and :186-189/:192-195 (dual tunnel — a plain
 * arrow BEFORE the label). They differ between the two layouts in legacy and
 * are kept that way here.
 *
 * The single-tunnel arrows are the Material Symbols `arrow_forward` /
 * `arrow_back` codepoints rather than U+27A1/U+2B05, and the stylesheet
 * appends "Material Symbols Outlined" to these selects' font stack so they
 * resolve (`.row-airflow-select` in ahuBoardOverrides.scss). The icon font
 * react.html already loads covers the full em box, so the arrow is drawn at
 * the same size and on the same baseline as the label in BOTH states, closed
 * control and open option list.
 *
 * What they replace: U+27A1/U+2B05, which are in no font we ship, so each one
 * fell back to whatever the OS had and drew ~8px of ink against a 13px label.
 * That is what read as "too small", and a `<select>` cannot size one character
 * of its own label, so the fix is the glyph rather than CSS.
 *
 * Dual keeps its inline U+2192/U+2190, which Roboto does have.
 */
const AIRFLOW_DIRECTIONS = [
  { id: 1, single: 'Left-to-Right \u2002\uE5C8', dual: '\u2192 Left-to-Right' },
  { id: 2, single: 'Right-to-Left \u2002\uE5C4', dual: '\u2190 Right-to-Left' },
];

/**
 * Phase 6e1 — Insert/Duplicate Section popup (§9.2, insert_component.popup.js
 * + insert_component.popup.tmpl, markup faithful). ONE React popup root
 * ([NEG]#42 — no duplicate-id body append). Single + dual layouts, rows from
 * the §19.3 bindings builder, first row pre-chosen, INSERT gated until a row
 * is chosen, dblclick = INSERT, Escape = cancel.
 *
 * INSERT decodes orders per the legacy select math (selected option index →
 * `order = idx + 2`, Upstream Edge = 1) and fires `create:component:any`
 * (ctrl passthrough); duplicate mode POSTs /ahu/duplicate/section/ (§20.10
 * #104 — completion via syncdata/polling, refetch-driven here).
 */

export interface InsertPopupRequest {
  name: string;
  value: string;
  type: string; // 'single_tunnel' | 'dual_tunnel'
  duplicate?: boolean;
  sectionId?: number;
}

interface InsertSectionPopupProps {
  request: InsertPopupRequest;
  ahu: IAhuBoardUnit;
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
  cms: ICmsAhuDoc | undefined;
  isPowerUser: boolean;
  onClose: () => void;
}

const InsertSectionPopup = ({
  request,
  ahu,
  tunnels,
  sections,
  cms,
  isPowerUser,
  onClose,
}: InsertSectionPopupProps) => {
  const dispatch = useAppDispatch();
  const [duplicateSection] = useDuplicateSectionCeleryMutation();
  const [patchAhuUnit] = usePatchAhuUnitMutation();
  const [chosen, setChosen] = useState(0);
  const [orders, setOrders] = useState<Record<string, number>>({});

  const isDual = request.type === 'dual_tunnel';

  // Standard/Hybrid mode toggle (§4 insert_component.popup.js `initialize`).
  // Duplicate mode never offers hybrid — it targets one specific tunnel
  // (request.sectionId's) by construction.
  const hasAnySections = sections.length > 0;
  /**
   * `is_hybrid` is the choice made when the FIRST section went in, so an AHU
   * with no sections has not chosen yet — whatever it was before every section
   * was deleted off it. The server clears the flag on the delete paths
   * (api/rest_ahu/utils.py `reset_hybrid_mode_if_empty`); reading it as
   * "not chosen" here as well means the toggle comes back whether or not the
   * unit refetch has landed yet.
   */
  const modeChosen = hasAnySections && !!ahu.is_hybrid;
  const showStandardMode = !request.duplicate && !modeChosen;
  const showHybridMode =
    !request.duplicate && (modeChosen || (isPowerUser && !hasAnySections));
  /**
   * `self.mode = self.show_hybrid_mode ? "hybrid" : "standard"`
   * (insert_component.popup.js:123) — Hybrid wins whenever it is on offer, so
   * a Power User opening this on a still-empty non-hybrid AHU lands on Hybrid,
   * not Standard. Gating on `ahu.is_hybrid` alone left exactly that case on
   * Standard.
   */
  const [mode, setMode] = useState<'standard' | 'hybrid'>(
    showHybridMode ? 'hybrid' : 'standard'
  );
  const isHybridMode = showHybridMode && (mode === 'hybrid' || !showStandardMode);

  const [hybridChosen, setHybridChosen] = useState(0);
  const [hybridDirections, setHybridDirections] = useState<Record<string, number>>({});
  /**
   * Typed X-Offset overrides, keyed `rowIdx-column`, held as the raw string so
   * a half-typed "12." survives a keystroke. A row with no entry shows its
   * computed default — and legacy's `applyXOffsetSignToAllRows` OVERWRITES
   * every input whenever the sign or a direction changes, so those handlers
   * clear the overrides rather than trying to preserve them.
   */
  const [hybridOffsets, setHybridOffsets] = useState<Record<string, string>>({});
  /** `self.xOffsetSign` — ONE toggle beside the column header, not per row. */
  const [xOffsetSign, setXOffsetSign] = useState<XOffsetSign>('plus');

  const sectionsByTunnel = useMemo(() => {
    const map: Record<number, IAhuBoardSection[]> = {};
    tunnels.forEach((t) => {
      map[t.id] = sections
        .filter((s) => s.tunnel_id === t.id)
        .sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0));
    });
    return map;
  }, [tunnels, sections]);

  const hybridBindings = useMemo(
    () =>
      isHybridMode
        ? getPossibleHybridBindings({ isDualTunnel: isDual, tunnels, sectionsByTunnel, cms })
        : [],
    [isHybridMode, isDual, tunnels, sectionsByTunnel, cms]
  );

  const bindings = useMemo(
    () =>
      getPossibleBindingsForInsertion({
        isDualTunnel: isDual,
        tunnels,
        sectionsByTunnel,
        cms,
      }),
    [isDual, tunnels, sectionsByTunnel, cms]
  );

  // Escape = cancel (§9.2).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keyup', onKey);
    return () => document.removeEventListener('keyup', onKey);
  }, [onClose]);

  const selectedOrder = (binding: InsertionBinding, column: number): number => {
    const key = `${bindings.indexOf(binding)}-${column}`;
    if (key in orders) return orders[key];
    // defaults to last (§9.2).
    return binding.occupied_tunnel_sections_last_index[column] ?? -1;
  };

  const noteBusy = (on: boolean) =>
    document
      .querySelector('.duplicating-section-note')
      ?.classList[on ? 'add' : 'remove']('active');

  /**
   * `section.duplicate` (section/model/section.js:451-520). The target tunnel is
   * whatever the chosen row's tunnel INDEX resolves to — and legacy resolves it
   * through `api.tunnel.get_or_create_tunnel_from_index`, which CREATES the
   * tunnel when the AHU has none at that index yet.
   *
   * That resolve is what was missing. React looked the index up in the tunnels
   * it already had and, on a miss, fell back to `source.tunnel_id` — so every
   * duplicate aimed at a tunnel that did not exist yet (the common case: the
   * popup lists indexes the resulting configuration allows, not just live
   * tunnels) landed back in the section's OWN tunnel, which is the reported bug.
   */
  const runDuplicate = async (
    source: IAhuBoardSection,
    block: { tunnel_index_id: number; order: number }
  ) => {
    noteBusy(true);
    const resolved = await dispatch(
      getOrCreateTunnelFromIndex(block.tunnel_index_id, { ahuId: ahu.id })
    );
    if (!resolved) {
      noteBusy(false);
      return;
    }
    const targetTunnelId = resolved.tunnel.id;
    // Sections already in the target BEFORE the duplicate — the poll below
    // identifies the new row as the one that is not in this set (legacy's
    // `ids` closure). A tunnel just created is empty, so any row is fresh.
    const knownIds = new Set((sectionsByTunnel[targetTunnelId] ?? []).map((s) => s.id));
    try {
      await duplicateSection({
        section_id: source.id,
        tunnel_id: targetTunnelId,
        order_rate: block.order,
        // legacy `new_tunnel: !tunnel.is_not_new` — true only when the resolve
        // above had to create the tunnel. It is the flag SectionDuplicateView
        // (ahu/views.py:150-167) uses to copy width/height/base/lug attachment/
        // floor/casing from the SOURCE tunnel onto the new one; sending it with
        // the source's own id (as the fallback did) made that copy a no-op.
        new_tunnel: resolved.created,
      }).unwrap();
    } catch {
      /* server refused — the poll below still reconciles, as legacy did */
    }
    if (resolved.created && resolved.tunnel.base_id) {
      void dispatch(createLugsForNewTunnel(targetTunnelId));
    }

    // §20.10 #104 — completion via syncdata action.fetch; the client
    // ALSO polls until the new section appears (legacy nested
    // 500/300 ms retry timers — bounded refetch loop here).
    let tries = 0;
    const poll = () => {
      tries += 1;
      dispatch(
        ahuBoardApi.endpoints.getTunnelSectionList.initiate(targetTunnelId, {
          forceRefetch: true,
        })
      )
        .unwrap()
        .then((rows) => {
          const fresh = rows.find((r) => !knownIds.has(r.id));
          if (fresh) {
            noteBusy(false);
            track('section_duplicated', { section_type: analyticsComponentName(source.type) });
            // Tunnel length recalced server-side by the duplicate —
            // refetch so the canvas props cascade sees it (legacy gets
            // this via the tunnel syncdata socket). Not awaited, but the
            // canvas pass below is held for it (canvasRedrawScheduler), so a
            // duplicate lands in ONE draw carrying the grown tunnel rather
            // than drawing the section and then re-drawing the tunnel.
            holdCanvasRedrawBatchFor(
              dispatch(
                ahuBoardApi.endpoints.getAhuTunnelList.initiate(ahu.id, {
                  forceRefetch: true,
                })
              )
            );
            // The duplicate grew the target tunnel, so its lug placement has
            // moved — re-read it, as the insert path does. On hybrid the
            // server re-laid the runs in this same request; on a standard AHU
            // the re-lay happens inside the placement GET itself
            // (refetchLugPlacementsAfterSectionInsert).
            holdCanvasRedrawBatchFor(
              dispatch(refetchLugPlacementsAfterSectionInsert(ahu.id, [targetTunnelId]))
            );
            ahuBoardBus.emit('added:new:component', fresh);
            ahuBoardBus.emit('show:on:canvas', fresh);
            ahuBoardBus.emit('select:section', { section: { id: fresh.id } });
            ahuBoardBus.emit('redraw:canvas');
            // `ahu.validateTunnelOnlyComponents({tunnelId})` (section.js:508) —
            // the duplicate changes the target tunnel's component set, and on a
            // freshly created tunnel it is the only thing that seeds its errors.
            void dispatch(validateTunnelOnlyComponents({ tunnelId: targetTunnelId }));
          } else if (tries < 12) {
            window.setTimeout(poll, tries < 3 ? 500 : 1000);
          } else {
            noteBusy(false);
          }
        })
        .catch(() => {
          if (tries < 12) window.setTimeout(poll, 1000);
        });
    };
    window.setTimeout(poll, 500);
  };

  const insert = (e?: React.MouseEvent) => {
    const binding = bindings[chosen];
    if (!binding || (isDual && binding.active === false)) return;
    const isCtrl = !!e?.ctrlKey;
    if (!isCtrl) {
      dispatch(clearSelection());
      ahuBoardBus.emit('remove:selected:section');
    }
    ahuBoardBus.emit('select:accessory', null);

    const blocks = binding.occupied_tunnel_indexes.map((index, column) => {
      const selIdx = selectedOrder(binding, column);
      // legacy decode: selected option's next sibling value + 2 → here
      // order = selected section index + 2 (Upstream Edge −1 → 1).
      const order = selIdx < 0 ? 1 : selIdx + 2;
      return {
        component_name: request.name,
        component_value: request.value,
        tunnel_index_id: Number(index.id),
        order,
        isCtrlPressed: isCtrl,
      };
    });

    if (request.duplicate && request.sectionId != null) {
      const source = sections.find((s) => s.id === request.sectionId);
      if (source) void runDuplicate(source, blocks[0]);
    } else {
      ahuBoardBus.emit('create:component:any', blocks);
    }
    onClose();
  };

  /**
   * A row's own airflow direction. Dual rows carry two selects that share one
   * X-Offset input, so the bottom/primary side's direction is what the offset
   * math follows (`getRowAirflowDirectionId`, insert_component.popup.js:231).
   */
  const hybridDirection = (bindingIdx: number, column: number) =>
    hybridDirections[`${bindingIdx}-${column}`] ??
    hybridBindings[bindingIdx]?.targets[column]?.defaultAirflowDirectionId ??
    1;

  /**
   * `getAddedSectionLength` (insert_component.popup.js:208) — the length of
   * the section about to be inserted, which is how far the "−" default has to
   * reach back so the new tunnel's downstream edge lands exactly on the
   * existing chain's start.
   */
  const addedSectionLength = (): number => {
    if (request.sectionId != null) {
      const source = sections.find((s) => s.id === request.sectionId);
      if (source) return parseFloat(String(source.length ?? 0)) || 0;
    }
    return DEFAULT_SECTION_LENGTH[request.value] ?? DEFAULT_SECTION_LENGTH.plenum;
  };

  /**
   * `getDefaultXOffset` (insert_component.popup.js:262) — the X-Offset a
   * hybrid row defaults to: butt the new tunnel against the rightmost peer's
   * right edge for "+", or the leftmost peer's left edge for "−", reading each
   * peer's own physical span so a mixed-direction chain still resolves. A
   * row:level with no peers yet is a brand-new independent chain and starts
   * fresh at X = 0 rather than inheriting some other row's bounds.
   */
  const defaultXOffset = (bindingIdx: number, column: number, sign: XOffsetSign): number => {
    const target = hybridBindings[bindingIdx]?.targets[column];
    if (!target) return 0;
    const peers = tunnels
      .filter((t) => t.row === target.row && t.level === target.level)
      .map(getTunnelPhysicalSpan)
      .sort((a, b) => a.left - b.left);
    const extensionPoint = peers.length
      ? sign === 'plus'
        ? peers[peers.length - 1].right
        : peers[0].left
      : 0;
    // Dual rows share the bottom side's direction for the offset math.
    const directionId = hybridDirection(bindingIdx, 0);
    return defaultOffsetForNewTunnel(
      addedSectionLength(),
      directionId,
      extensionPoint,
      sign
    );
  };

  /** The UNSIGNED magnitude an X-Offset input shows — typed value if the user
   * has touched it, else the computed default, always to 2dp. */
  const hybridOffsetValue = (bindingIdx: number, column: number): string => {
    const typed = hybridOffsets[`${bindingIdx}-${column}`];
    if (typed != null) return typed;
    return Math.abs(defaultXOffset(bindingIdx, column, xOffsetSign)).toFixed(2);
  };

  /**
   * `onXOffsetSignClick` — the toggle is dialog-wide, and switching it
   * recomputes EVERY row (`applyXOffsetSignToAllRows`), discarding typed
   * values. Clearing the override map is that same overwrite.
   */
  const onSignClick = (sign: XOffsetSign) => {
    if (sign === xOffsetSign) return;
    setXOffsetSign(sign);
    setHybridOffsets({});
  };

  /**
   * `onRowAirflowChange` (insert_component.popup.js:361) — the X-Offset
   * default depends on the row's own direction, so flipping DIRECTION
   * recomputes it.
   *
   * The auto-flip: for a row:level whose tunnels hold no sections yet,
   * `getDefaultXOffset`'s extension point is always 0, and
   * `defaultOffsetForNewTunnel` only resolves that to the natural X = 0 start
   * when the sign matches the row's own direction — "+" for Left-to-Right,
   * "−" for Right-to-Left. The toggle defaults to "+", so picking
   * Right-to-Left on a brand-new row would otherwise sit stuck on a spurious
   * ±length default. Deliberately a test for "no SECTIONS at this row:level"
   * rather than "no tunnel there": an emptied AHU keeps neither, but one that
   * still has its starter tunnel is just as much a first section.
   */
  const onRowAirflowChange = (bindingIdx: number, column: number, value: number) => {
    setHybridChosen(bindingIdx);
    setHybridDirections((prev) => ({ ...prev, [`${bindingIdx}-${column}`]: value }));
    const target = hybridBindings[bindingIdx]?.targets[column];
    const isFirstSection =
      !!target &&
      !tunnels.some(
        (t) =>
          t.row === target.row &&
          t.level === target.level &&
          (sectionsByTunnel[t.id] ?? []).length > 0
      );
    if (isFirstSection) {
      const desiredSign: XOffsetSign = value === 2 ? 'minus' : 'plus';
      if (desiredSign !== xOffsetSign) setXOffsetSign(desiredSign);
    }
    setHybridOffsets({});
  };

  /**
   * Hybrid-mode INSERT (§4 insertComponent). If this is the first section on
   * the whole AHU, PATCH the AHU to hybrid FIRST and await it — the
   * tunnel-create PATCH below unconditionally re-saves the whole AHU row
   * (AHUUnit.save side effects), so creating the tunnel before this lands
   * would clobber is_hybrid back to false.
   *
   * Sent whenever the AHU is empty rather than only when `ahu.is_hybrid` is
   * false: the server clears the flag once the last section goes, so a cached
   * `true` here would skip the PATCH and leave the rebuilt unit flagged
   * Standard in the database.
   */
  const insertHybrid = async (e?: React.MouseEvent) => {
    const row = hybridBindings[hybridChosen];
    if (!row) return;
    const isCtrl = !!e?.ctrlKey;
    if (!isCtrl) {
      dispatch(clearSelection());
      ahuBoardBus.emit('remove:selected:section');
    }
    ahuBoardBus.emit('select:accessory', null);

    if (!hasAnySections) {
      try {
        await patchAhuUnit({
          id: ahu.id,
          data: { is_hybrid: true, ...(ahu.anchor == null ? { anchor: 0 } : {}) },
        }).unwrap();
      } catch {
        return;
      }
    }

    // Both tunnels of a dual pair share the one X-Offset input, and the input
    // holds an unsigned magnitude — the header toggle's sign is applied here.
    const xOffset = applySign(Number(hybridOffsetValue(hybridChosen, 0)), xOffsetSign);

    const blocks = row.targets.map((target, column) => {
      const hybridTarget: HybridTunnelTarget = {
        row: target.row,
        level: target.level,
        position: target.position,
        index_id: target.index_id,
        existingTunnelId: target.existingTunnelId,
        // `addressing` set ⇒ legacy's get_or_create always CREATES; the same
        // legacy index_id repeats across back-to-back peers, so an
        // address lookup would hand back the row's lead tunnel instead.
        alwaysCreate: target.existingTunnelId == null,
        peerTunnelId: target.peerTunnelId,
        x_offset: xOffset,
        airflow_direction_id: hybridDirection(hybridChosen, column),
      };
      return {
        component_name: request.name,
        component_value: request.value,
        tunnel_index_id: target.index_id,
        // Hybrid rows carry no "downstream of" select — the new tunnel is
        // empty, so the section is always its first (`orders[i] || 1`).
        order: 1,
        isCtrlPressed: isCtrl,
        hybrid: hybridTarget,
      };
    });

    ahuBoardBus.emit('create:component:any', blocks);
    onClose();
  };

  /**
   * `current_configuration` (insert_component.popup.js:178). Legacy
   * configurations come back as "1 x 1", "1 x 2"… and are reformatted to
   * "1 W x 1 H" for display. A hybrid configuration comes back as the bare
   * label "Hybrid" (no " x " substring) and is kept as-is; null — possible
   * when the master row is missing — also reads "Hybrid".
   */
  const currentConfig = (() => {
    const cfg = ahu.tunnel_configuration;
    if (!cfg) return 'Hybrid';
    return cfg.includes(' x ') ? `${cfg.replace(' x ', ' W x ')} H` : cfg;
  })();

  const renderSelect = (binding: InsertionBinding, column: number, bindingIdx: number) => {
    const names = binding.occupied_tunnel_sections[column];
    if (!names || !binding.has_sections) {
      return <span className="upstrem_edge">Upstream Edge</span>;
    }
    return (
      <select
        className="default"
        value={String(selectedOrder(binding, column))}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) =>
          setOrders((prev) => ({
            ...prev,
            [`${bindingIdx}-${column}`]: parseInt(e.currentTarget.value, 10),
          }))
        }
      >
        <option value="-1">Upstream Edge</option>
        {names.map((n, i) => (
          <option key={`${n}-${i}`} value={i}>
            {n}
          </option>
        ))}
      </select>
    );
  };

  /**
   * The "X-OFFSET (in) + / −" column header's sign toggle — one per dialog,
   * rendered inside the header cell (insert_component.popup.tmpl:46-53 single,
   * :139-145 dual). The dual heading has no `.seperator` between the two
   * options; the single one does.
   */
  const renderSignToggle = (withSeparator: boolean) => (
    <span className="row-x-offset-sign-toggle">
      <span
        className={`row-x-offset-sign-option plus${xOffsetSign === 'plus' ? ' active' : ''}`}
        data-sign="plus"
        onClick={(e) => {
          e.stopPropagation();
          onSignClick('plus');
        }}
      >
        +
      </span>
      {withSeparator && <span className="seperator">/</span>}
      <span
        className={`row-x-offset-sign-option minus${xOffsetSign === 'minus' ? ' active' : ''}`}
        data-sign="minus"
        onClick={(e) => {
          e.stopPropagation();
          onSignClick('minus');
        }}
      >
        {'−'}
      </span>
    </span>
  );

  /**
   * A row's X-Offset cell: the sign prefix echoing the header toggle, plus the
   * unsigned magnitude input. `onclick`/`ondblclick` are stopped so a click in
   * the input doesn't bubble to `.ka-row` and re-fire chooseTunnel — and focus
   * explicitly marks the row chosen, or typing in row A's offset while row B
   * is `.choose`n would insert into B with A's values.
   */
  const renderXOffsetCell = (bindingIdx: number) => (
    <li className="medium x-offset-col">
      <span className="row-x-offset-sign-prefix">
        {xOffsetSign === 'minus' ? '−' : '+'}
      </span>
      <input
        className="row-x-offset-input"
        type="text"
        data-type="number"
        data-formatter_type="decimal"
        data-step="0.01"
        data-min="0"
        value={hybridOffsetValue(bindingIdx, 0)}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onFocus={() => setHybridChosen(bindingIdx)}
        onChange={(e) =>
          setHybridOffsets((prev) => ({ ...prev, [`${bindingIdx}-0`]: e.currentTarget.value }))
        }
        onKeyDown={(e) => {
          // The input is an unsigned magnitude — the sign comes from the
          // header toggle only, so typed '+'/'-' are blocked rather than
          // accepted and stripped. Enter submits (the row is already chosen).
          if (e.key === '+' || e.key === '-') {
            e.preventDefault();
            return;
          }
          if (e.key !== 'Enter') return;
          e.preventDefault();
          void insertHybrid();
        }}
      />
    </li>
  );

  const renderAirflowCell = (
    bindingIdx: number,
    column: number,
    className: string,
    selectClass: string
  ) => (
    <li className={className}>
      <select
        className={selectClass}
        value={hybridDirection(bindingIdx, column)}
        onClick={(e) => e.stopPropagation()}
        onFocus={() => setHybridChosen(bindingIdx)}
        onChange={(e) => onRowAirflowChange(bindingIdx, column, Number(e.currentTarget.value))}
      >
        {AIRFLOW_DIRECTIONS.map((d) => (
          <option key={d.id} value={d.id}>
            {isDual ? d.dual : d.single}
          </option>
        ))}
      </select>
    </li>
  );

  return (
    // Class list verbatim from insert_component.popup.tmpl:1 — in particular
    // NO `active`. This popup is visible without it: ahu/styles.css:6642
    // `.location-popup.tunnel-popup { display: block !important }` outranks
    // common.css:27540 `.popup-wrapper { display: none !important }` on
    // specificity, which is exactly why legacy's template never set it.
    //
    // Adding `active` switched on common.css:27554
    // `.popup-wrapper.active .popup-content { animation-name: easePopup }`, and
    // easePopup (:28237) is `scale(0,0) → scale(1,1)` with NO translate — so for
    // its 0.3s it overrode the static `transform: translate(-50%, -30%)` that
    // centres the dialog (ahu/styles.css:6645). Measured: the box rendered at
    // x=662,y=269 for 300ms and then snapped to x=322,y=154 — a 340×115px jump
    // that reads as the dialog closing and reopening. `#insert_component_popup`
    // is the host div BoardContent already renders (legacy's Backbone `el`,
    // body-appended in pre_render), so emitting it again here only duplicated
    // the DOM id.
    <div
      className={`popup-wrapper tunnel-popup location-popup${
        isDual ? ' dual-tunnel ' : ''
      }`}
    >
      <div className="popup-content popup-content-animation">
        <h2 className="popup-heading">
          {/* The heading text is its own span so the h2's `space-between`
              has two flex items to push apart; the toggle is always rendered
              when EITHER mode applies — as a lone, non-interactive label when
              only one does (insert_component.popup.tmpl:3-23). */}
          <span className="popup-heading-text">
            {request.duplicate ? ' Duplicate ' : ' ADD '}Section
          </span>
          {(showStandardMode || showHybridMode) && (
            <span
              className={`tunnel-mode-toggle${
                showStandardMode && showHybridMode ? ' interactive' : ''
              }`}
            >
              {showStandardMode && (
                <span
                  className={`tunnel-mode-option${
                    !showHybridMode || mode === 'standard' ? ' active' : ''
                  }`}
                  data-mode="standard"
                  onClick={() => setMode('standard')}
                >
                  Standard
                </span>
              )}
              {showHybridMode && (
                <span
                  className={`tunnel-mode-option${
                    !showStandardMode || mode === 'hybrid' ? ' active' : ''
                  }`}
                  data-mode="hybrid"
                  onClick={() => setMode('hybrid')}
                >
                  Hybrid
                </span>
              )}
            </span>
          )}
        </h2>
        <div className="popup-content-part accss-section">
          <div>
            <span className="section-name">Section :</span>
            <span className="section-tag"> {request.name}</span>
          </div>
          <div>
            <span className="section-name">Section type :</span>
            <span className="section-tag"> {isDual ? 'Dual Tunnel' : 'Single Tunnel'}</span>
            <span className="section-config no-wrap">Current AHU Configuration :</span>
            <span className="section-config-value right-48">{currentConfig}</span>
          </div>
        </div>
        {isHybridMode ? (
          <div className="popup-content-part">
            <div className={isDual ? 'dual-tunnel' : 'single-tunnel'}>
              <div>
                <ul className="list-heading">
                  <li className="small-126">ROW : LEVEL</li>
                  <li className="medium x-offset-col">
                    X-OFFSET (in)
                    {renderSignToggle(!isDual)}
                  </li>
                  {isDual ? (
                    <>
                      <li className="medium">TUNNEL 1 DIRECTION</li>
                      <li className="medium">TUNNEL 2 DIRECTION</li>
                    </>
                  ) : (
                    <li className="long">DIRECTION</li>
                  )}
                </ul>
                <ul className="list-rows">
                  {hybridBindings.map((row, idx) => (
                    <li
                      key={idx}
                      className={`ka-row${hybridChosen === idx ? ' choose' : ''}${
                        row.isBackToBack ? ' back-to-back-row' : ''
                      }`}
                      data-back-to-back-row={row.targets[0].row}
                      data-back-to-back-level={row.targets[0].level}
                      onClick={() => setHybridChosen(idx)}
                      onDoubleClick={(e) => {
                        setHybridChosen(idx);
                        void insertHybrid(e);
                      }}
                    >
                      <ul>
                        <li className="small-126" data-id={row.targets[0].index_id}>
                          {row.label}
                        </li>
                        {renderXOffsetCell(idx)}
                        {isDual ? (
                          <>
                            {renderAirflowCell(idx, 0, 'medium', 'row-airflow-select-1')}
                            {renderAirflowCell(idx, 1, 'medium', 'row-airflow-select-2')}
                            <li data-id={row.targets[1].index_id} style={{ display: 'none' }} />
                          </>
                        ) : (
                          renderAirflowCell(idx, 0, 'long', 'row-airflow-select')
                        )}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="popup-content-part">
            <div className={isDual ? 'dual-tunnel' : 'single-tunnel'}>
              <div>
                <ul className="list-heading">
                  <li className="small-126">ADD IN TUNNEL</li>
                  <li className="medium">ADD DOWNSTREAM OF</li>
                  {isDual && (
                    <>
                      <li className="small small-to">- to -</li>
                      <li className="small">TUNNEL</li>
                      <li className="medium downstream-of-secondary">DOWNSTREAM OF</li>
                    </>
                  )}
                  <li className="long">RESULTING AHU CONFIGURATION</li>
                </ul>
                <ul className="list-rows">
                  {bindings.map((binding, idx) => (
                    <li
                      key={idx}
                      className={`ka-row${chosen === idx ? ' choose' : ''}${
                        isDual && binding.active === false ? ' non-clickable' : ''
                      }`}
                      onClick={() => setChosen(idx)}
                      onDoubleClick={(e) => {
                        setChosen(idx);
                        insert(e);
                      }}
                    >
                      <ul>
                        <li
                          className="small-126"
                          data-id={binding.occupied_tunnel_indexes[0]?.id}
                        >
                          {String(binding.occupied_tunnel_indexes[0]?.name ?? '')}
                        </li>
                        <li className="medium">{renderSelect(binding, 0, idx)}</li>
                        {isDual && (
                          <>
                            <li className="small small-to" />
                            <li
                              className="small"
                              data-id={binding.occupied_tunnel_indexes[1]?.id}
                            >
                              {String(binding.occupied_tunnel_indexes[1]?.name ?? '')}
                            </li>
                            <li className="medium downstream-of-secondary">{renderSelect(binding, 1, idx)}</li>
                          </>
                        )}
                        <li className="long">
                          {String(binding.resulting_configuration?.config_name ?? '')}
                        </li>
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        <div className="popup-control-buttons">
          <button
            className={`btn insert${
              (isHybridMode ? hybridBindings.length : bindings.length) ? '' : ' non-clickable'
            }`}
            autoFocus
            onClick={(e) => (isHybridMode ? void insertHybrid(e) : insert(e))}
          >
            INSERT
          </button>
          <button className="btn cancel" onClick={onClose}>
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsertSectionPopup;
